import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { LeadSection } from "@/components/LeadSection";
import { Section, Eyebrow, CTA } from "@/components/kit";
import { Reveal } from "@/components/motion";
import { DuoBanner } from "@/components/DuoBanner";
import { NetworkLogo } from "@/components/NetworkLogo";
import { BroadcastModeBanner } from "@/components/BroadcastModeBanner";
import { LiveSecondScreen } from "@/components/LiveSecondScreen";
import { CommercialDynamics } from "@/components/CommercialDynamics";
import { useBroadcast } from "@/context/BroadcastContext";
import { Radio, Calendar, Tv } from "lucide-react";
import { SCHEDULE, EPISODES, IMG } from "@/data/content";

// Próximo sábado 20:00
function nextSaturday() {
  const now = new Date();
  const d = new Date(now);
  const day = d.getDay();
  let add = (6 - day + 7) % 7;
  d.setHours(20, 0, 0, 0);
  if (add === 0 && now > d) add = 7;
  d.setDate(d.getDate() + add);
  return d;
}

const Countdown = () => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = nextSaturday();
    const tick = () => {
      const diff = Math.max(0, target - new Date());
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const box = (v, l) => (
    <div className="flex flex-col items-center">
      <span className="split-flap min-w-[64px] rounded-xl px-3 py-3 text-center font-mono text-3xl sm:text-4xl font-bold text-gold">
        {String(v).padStart(2, "0")}
      </span>
      <span className="mt-2 font-mono text-[10px] uppercase tracking-widest text-cream/50">
        {l}
      </span>
    </div>
  );
  return (
    <div className="flex gap-3 sm:gap-4">
      {box(t.d, "Días")}
      {box(t.h, "Hrs")}
      {box(t.m, "Min")}
      {box(t.s, "Seg")}
    </div>
  );
};

export default function EnVivo() {
  const now = EPISODES.find((e) => e.statusType === "now") || EPISODES[0];
  const { state } = useBroadcast();
  const [demoState, setDemoState] = useState(null);
  const displayState = demoState || state;
  const simulateCommercials = (seconds) => {
    setDemoState({
      ...state,
      mode: "commercials",
      dynamic: "trivia",
      commercial_ends_at: new Date(Date.now() + seconds * 1000).toISOString(),
      source: "demo",
    });
  };
  useEffect(() => {
    if (!demoState?.commercial_ends_at) return undefined;
    const remaining = new Date(demoState.commercial_ends_at).getTime() - Date.now();
    const timeout = window.setTimeout(() => setDemoState(null), Math.max(0, remaining));
    return () => window.clearTimeout(timeout);
  }, [demoState]);
  return (
    <>
      <PageHeader
        eyebrow="Transmisión · En Vivo · SINGULATIVA-TV.PRO"
        eyebrowColor="#FF8A80"
        title={["Nos vemos siempre", "en Singulativa TV."]}
        subtitle="Cada episodio abrimos la puerta al mundo. Si estás en la zona Occidente de México, sintoniza la señal abierta. Aquí decimos dónde y cuándo. Nada de plataformas confusas: TV abierta, redes y, muy pronto, la app."
      />

      <Section className="!pt-6">
        <BroadcastModeBanner state={displayState} />
        <div data-testid="live-broadcast-simulator" className="mt-4 flex flex-wrap items-center justify-between gap-3 border-y border-ink/10 py-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink/45">Singulativa TV · 24 horas</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="live-simulator-live-button" onClick={() => setDemoState(null)} className="rounded-full bg-ink px-3 py-2 text-xs font-semibold text-cream transition-colors hover:bg-coral">Programa al aire</button>
            <button type="button" data-testid="live-simulator-commercial-30-button" onClick={() => simulateCommercials(30)} className="rounded-full bg-gold px-3 py-2 text-xs font-semibold text-ink transition-colors hover:bg-[#f4d87c]">Comerciales · 30s</button>
            <button type="button" data-testid="live-simulator-commercial-60-button" onClick={() => simulateCommercials(60)} className="rounded-full bg-gold px-3 py-2 text-xs font-semibold text-ink transition-colors hover:bg-[#f4d87c]">Comerciales · 60s</button>
          </div>
        </div>
        <Reveal>
          <div className="overflow-hidden rounded-[2rem] bg-ink shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
            <div className="flex items-center gap-2 border-b border-white/10 px-6 py-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-coral opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-coral" />
              </span>
              <span className="font-mono text-xs uppercase tracking-widest text-coral">
                {displayState.mode === "commercials" ? "TV en comerciales · segunda pantalla activa" : `En vivo · Ep ${now.n} · ${now.city} · SINGULATIVA-TV`}
              </span>
            </div>
            <div className="aspect-video w-full">
              <iframe
                data-testid="live-player"
                className="h-full w-full"
                src="https://www.youtube-nocookie.com/embed/I2dLt28rM1U?rel=0&modestbranding=1"
                title="Anto&Bella por el Mundo — Video demo comercial"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </Reveal>
        <div className="mt-6">
          {displayState.mode === "commercials" ? <CommercialDynamics dynamic={displayState.dynamic} /> : <LiveSecondScreen />}
        </div>
      </Section>

      <Section alt className="!pt-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal className="rounded-[2rem] bg-ink p-9 text-cream">
            <Eyebrow color="#A8E6CF">Cuenta regresiva</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight">
              Próximo estreno
            </h2>
            <p className="mb-7 mt-1 text-cream/60">Sábado, 20:00 (hora del centro)</p>
            <Countdown />
          </Reveal>

          <Reveal delay={0.1} className="rounded-[2rem] bg-white p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <Eyebrow color="#81D4FA">Dónde ver</Eyebrow>
            <div className="mt-6 space-y-5">
              {[
                { icon: Tv, t: SCHEDULE.main, d: SCHEDULE.region },
                { icon: Calendar, t: "Retransmisión", d: "¿Te lo perdiste? El domingo repetimos el episodio para que no quede nadie fuera. Domingo 11:00" },
                { icon: Radio, t: "Canal de Stregaming Oficial", d: "Singulativa TV" },
                { icon: Radio, t: "¿No puedes esperar al sábado?", d: "Instagram, YouTube y TikTok. Clips, Mientras llega el próximo episodio, mira los mejores momentos en nuestras redes. Y si quieres verlo todo desde el principio, la app tiene cada capítulo listo para ti." },              ].map((r) => (
                <div key={r.t} className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mist">
                    <r.icon className="h-5 w-5 text-coral" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-bold text-ink">{r.t}</p>
                    <p className="text-sm text-ink/60">{r.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl bg-mist p-4">
              <NetworkLogo theme="light" compact />
            </div>
            <CTA to="/vip" variant="coral" className="mt-8">
              Ver sin cortes en VIP
            </CTA>
          </Reveal>
        </div>
      </Section>

      <DuoBanner
        image={IMG.duoLive}
        imgSide="left"
        tint="#FF8A80"
        eyebrowColor="#FF8A80"
        title="¡Estamos al aire! ¿Nos acompañas?"
        text="Anto y Bella te esperan cada sábado a las 20:00. Enciende la tele, abre las redes y viaja con nosotras en tiempo real."
        cta={{ to: "/believers", label: "Ser Believer", variant: "ink" }}
      />
      <LeadSection path="/en-vivo" alt />
    </>
  );
}
